import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  LogIn, BookOpen, Compass, Trophy, HelpCircle, Star, MessageCircle,
  MessagesSquare, Award, Crown, BookMarked, ThumbsUp, Lock
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'log-in': LogIn,
  'book-open': BookOpen,
  'compass': Compass,
  'trophy': Trophy,
  'help-circle': HelpCircle,
  'star': Star,
  'message-circle': MessageCircle,
  'messages-square': MessagesSquare,
  'award': Award,
  'crown': Crown,
  'book-marked': BookMarked,
  'thumbs-up': ThumbsUp,
};

const categoryLabels = {
  fr: { onboarding: 'Demarrage', progress: 'Progression', quiz: 'Quiz', engagement: 'Engagement', achievement: 'Accomplissement' },
  en: { onboarding: 'Onboarding', progress: 'Progress', quiz: 'Quiz', engagement: 'Engagement', achievement: 'Achievement' },
};

export default function BadgesPage() {
  const { language, t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [badgesData, setBadgesData] = useState({ badges: [], new_badges: [], total_earned: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showNewBadge, setShowNewBadge] = useState(null);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await axios.get(`${API}/badges`, { headers: getAuthHeaders(), withCredentials: true });
        setBadgesData(res.data);
        // Show celebration for newly earned badges
        if (res.data.new_badges?.length > 0) {
          const newBadge = res.data.badges.find(b => b.badge_id === res.data.new_badges[0]);
          if (newBadge) setShowNewBadge(newBadge);
        }
      } catch (err) {
        console.error('Badges fetch error:', err);
      }
      setLoading(false);
    };
    fetchBadges();
  }, [getAuthHeaders]);

  const totalBadges = badgesData.badges.length;
  const earnedBadges = badgesData.total_earned;
  const progressPct = totalBadges > 0 ? Math.round((earnedBadges / totalBadges) * 100) : 0;

  // Group by category
  const categories = [...new Set(badgesData.badges.map(b => b.category))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="badges-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg" data-testid="badges-logo" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="badges-title">
              {language === 'fr' ? 'Mes Badges' : 'My Badges'}
            </h1>
          </div>
          <p className="text-slate-500 mt-2 text-base md:text-lg">
            {language === 'fr' ? 'Collectionnez des badges en progressant dans votre formation' : 'Collect badges as you progress in your training'}
          </p>
        </div>

        {/* Progress overview */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="badges-progress">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>
                  {earnedBadges}/{totalBadges} {language === 'fr' ? 'badges obtenus' : 'badges earned'}
                </p>
                <p className="text-sm text-slate-500">{progressPct}% {language === 'fr' ? 'de la collection' : 'of collection'}</p>
              </div>
            </div>
            <span className="text-3xl font-bold text-amber-600" style={{ fontFamily: 'Poppins' }}>{earnedBadges}</span>
          </div>
          <Progress value={progressPct} className="h-2.5" />
        </div>

        {/* Badges by category */}
        {categories.map((cat) => {
          const catBadges = badgesData.badges.filter(b => b.category === cat);
          return (
            <div key={cat} className="mb-8" data-testid={`badge-category-${cat}`}>
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
                {categoryLabels[language]?.[cat] || cat}
                <Badge variant="outline" className="text-xs">
                  {catBadges.filter(b => b.earned).length}/{catBadges.length}
                </Badge>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {catBadges.map((badge) => {
                  const IconComp = iconMap[badge.icon] || Star;
                  return (
                    <button
                      key={badge.badge_id}
                      onClick={() => setSelectedBadge(badge)}
                      className={`group relative bg-white rounded-xl border p-5 text-center transition-shadow hover:shadow-md ${
                        badge.earned 
                          ? 'border-slate-200 cursor-pointer' 
                          : 'border-dashed border-slate-300 opacity-50 cursor-pointer'
                      }`}
                      data-testid={`badge-${badge.badge_id}`}
                    >
                      <div
                        className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110 ${
                          badge.earned ? '' : 'grayscale'
                        }`}
                        style={{
                          backgroundColor: badge.earned ? `${badge.color}15` : '#F1F5F9',
                        }}
                      >
                        {badge.earned ? (
                          <IconComp className="w-7 h-7" style={{ color: badge.color }} />
                        ) : (
                          <Lock className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <p className={`text-sm font-medium leading-tight ${badge.earned ? 'text-slate-900' : 'text-slate-400'}`}>
                        {language === 'fr' ? badge.name_fr : badge.name_en}
                      </p>
                      {badge.earned && badge.awarded_at && (
                        <p className="text-[10px] text-slate-400 mt-1">
                          {new Date(badge.awarded_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Badge Detail Dialog */}
      {selectedBadge && (
        <Dialog open={!!selectedBadge} onOpenChange={() => setSelectedBadge(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="badge-detail-dialog">
            <DialogHeader>
              <DialogTitle className="sr-only">
                {language === 'fr' ? selectedBadge.name_fr : selectedBadge.name_en}
              </DialogTitle>
            </DialogHeader>
            <div className="text-center py-4">
              <div
                className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                  selectedBadge.earned ? '' : 'grayscale opacity-50'
                }`}
                style={{ backgroundColor: selectedBadge.earned ? `${selectedBadge.color}15` : '#F1F5F9' }}
              >
                {(() => {
                  const Icon = iconMap[selectedBadge.icon] || Star;
                  return selectedBadge.earned 
                    ? <Icon className="w-10 h-10" style={{ color: selectedBadge.color }} />
                    : <Lock className="w-8 h-8 text-slate-400" />;
                })()}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
                {language === 'fr' ? selectedBadge.name_fr : selectedBadge.name_en}
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                {language === 'fr' ? selectedBadge.description_fr : selectedBadge.description_en}
              </p>
              {selectedBadge.earned ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  {language === 'fr' ? 'Obtenu le' : 'Earned on'}{' '}
                  {new Date(selectedBadge.awarded_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-slate-500">
                  {language === 'fr' ? 'Non obtenu' : 'Not earned yet'}
                </Badge>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* New Badge Celebration Dialog */}
      {showNewBadge && (
        <Dialog open={!!showNewBadge} onOpenChange={() => setShowNewBadge(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="new-badge-celebration">
            <DialogHeader>
              <DialogTitle className="sr-only">
                {language === 'fr' ? 'Nouveau badge !' : 'New badge!'}
              </DialogTitle>
            </DialogHeader>
            <div className="text-center py-6">
              <div className="relative">
                <div
                  className="w-24 h-24 mx-auto rounded-3xl flex items-center justify-center mb-4 animate-pulse-glow"
                  style={{ backgroundColor: `${showNewBadge.color}15` }}
                >
                  {(() => {
                    const Icon = iconMap[showNewBadge.icon] || Star;
                    return <Icon className="w-12 h-12" style={{ color: showNewBadge.color }} />;
                  })()}
                </div>
              </div>
              <p className="text-sm text-blue-600 font-semibold uppercase tracking-wide mb-1">
                {language === 'fr' ? 'Nouveau Badge !' : 'New Badge!'}
              </p>
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
                {language === 'fr' ? showNewBadge.name_fr : showNewBadge.name_en}
              </h3>
              <p className="text-sm text-slate-500">
                {language === 'fr' ? showNewBadge.description_fr : showNewBadge.description_en}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
